import { compressionCodecType, Dataset, LinkedService, Metamodel, Named, Owned } from 'shell-models-lib';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DatasetTypeHandler } from 'app/components/create-datamapper/utils/datasettype-handler';
import { IMapperConnectionInfo, IMapperEntity, IMapperTable } from 'app/model/adfDataMapper/IMapperTable';
import { DataFlowDatasetSettings, DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { OrcSettings } from 'app/model/dataflow/dataset-settings/orc-settings';
import { OrcDataset } from 'app/model/dataset/orc.dataset';
import { ensureAllPathsAreUnique, getAdlsDatasetForPreview, initializeCommonConnectionProperties, processEachADLSEntity } from 'app/components/create-datamapper/utils/adls-component-utils';
import { DslConnectorConstants } from 'app/model/adapters/mappingArtifact/DslConnectorConstants';
import { createDataflowExpression, DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { IDataMapperFileOptions } from 'app/model/adfDataMapper/IDataMapperFileOptions';
import { getDslPropertyValue } from 'app/model/adfDataMapper/mapperTable.utils';

export class OrcDatasetTypeHandler extends DatasetTypeHandler {

    constructor(
        public isSource: Boolean
     ){
         super();
     }

    public populateSourceProperties(inputSource: DataFlowSource, outputSource: DataFlowSource, selectedDatasetFormat: DatasetFormatType) {
        outputSource.datasetSettings = Owned.create(Metamodel.modelByName('OrcSettings'), outputSource);
        const incomingSourceDatasetsSettings = inputSource.datasetSettings as OrcSettings;
        const outputSourceDatasetsSettings = outputSource.datasetSettings as OrcSettings;
        outputSource.datasetSettings.initialize('');
        outputSourceDatasetsSettings.container.expression = !incomingSourceDatasetsSettings.container ? undefined : incomingSourceDatasetsSettings.container.expression;
        outputSourceDatasetsSettings.folderPath.expression = !incomingSourceDatasetsSettings.folderPath ? undefined : incomingSourceDatasetsSettings.folderPath.expression;
        outputSourceDatasetsSettings.compressionType = !incomingSourceDatasetsSettings.compressionType ? undefined : incomingSourceDatasetsSettings.compressionType;
        outputSource.wildcardPaths = inputSource.wildcardPaths;
        outputSource.datasetSettings = outputSourceDatasetsSettings;
    }

    public getOkButtonStatus(selectedRows: IMapperEntity[]): [boolean, string] {
        let disableButton = true;
        let disableButtonStatus = '';
        if (selectedRows.length) {
            disableButton = false;
            const rows = selectedRows as IMapperEntity[];
            rows.forEach((elem: IMapperEntity) => {
                const containerPath = (elem.dataset as OrcDataset)?.location?.getLocation()?.container;
                if (!containerPath) {
                    disableButton = true;
                }
            });
            if (!disableButton) {
                [disableButton, disableButtonStatus] = !ensureAllPathsAreUnique(rows) ? [true, stringResources.DuplicateSourceEntitySelected] : [false, ''];
            }
            return [disableButton, disableButtonStatus];
        }
        return [disableButton, disableButtonStatus];
    }

    public processSelectedRows(
        selectedRows: IMapperEntity[],
        selectedSourceLinkedService: LinkedService,
        selectedDatasetFormat: DatasetFormatType): IMapperTable[] {

        const tables: IMapperTable[] = [];
        selectedRows.forEach((elem) => {
            const filePath = (elem.dataset as OrcDataset).location.getLocation();
            processEachADLSEntity(filePath, selectedSourceLinkedService, elem, selectedDatasetFormat, tables, this.isSource);
        });

        return tables;
    }

    public getFileOptions(): IDataMapperFileOptions {
        return {
            compressionTypeOptions: [
                {
                    label: stringResources.None,
                    value: undefined
                },
                {
                    label: compressionCodecType.zlib,
                    value: compressionCodecType.zlib
                },
                {
                    label: compressionCodecType.lzo,
                    value: compressionCodecType.lzo
                },
                {
                    label: compressionCodecType.snappy,
                    value: compressionCodecType.snappy
                }
            ]
        };
    }

    public getDefaultFileSettings(): DataFlowDatasetSettings {
        const dataFlowSource = DataFlowSource.MODEL.create<Named>();
        const orcSettings: OrcSettings = Owned.create<OrcSettings>(OrcSettings.MODEL, dataFlowSource);
        orcSettings.initialize(undefined);
        return orcSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetSettings(dslConnectorProperties: Map<string, any>, table: IMapperTable): DataFlowDatasetSettings {
        // Read dsl properties
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        const compressionType = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.compressionCodec);

        // Create and populate OrcSettings
        const orcSettings = Owned.create<OrcSettings>(OrcSettings.MODEL, table as DataFlowSource);
        orcSettings.initialize(undefined);

        orcSettings.container = createDataflowExpression(container) as DataFlowExpression;
        orcSettings.folderPath = createDataflowExpression(folderPath) as DataFlowExpression;
        orcSettings.compressionType = compressionType ?? undefined;

        return orcSettings;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getDatasetForPreview(dslConnectorProperties: Map<string, any>, linkedService: LinkedService): Dataset {
        const container = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.container);
        const folderPath = getDslPropertyValue(dslConnectorProperties, DslConnectorConstants.folderPath);
        return getAdlsDatasetForPreview(DatasetFormatType.Orc, container, folderPath, linkedService);
    }

    public setCommonDslConnectionProperties(mapperConnectionInfo: IMapperConnectionInfo, selectedDatasetFormat: DatasetFormatType, selectedSourceFileOptions: DataFlowDatasetSettings): void {
        this.setCommonDslDriftProperties(mapperConnectionInfo);
        initializeCommonConnectionProperties(mapperConnectionInfo.connection, selectedDatasetFormat, this.isSource);
        // Add orc dataset settings
        const orcSettings = selectedSourceFileOptions as OrcSettings;
        if (this.isSource && !!orcSettings.compressionType) {
            mapperConnectionInfo.connection.commonDslConnectorProperties.set(DslConnectorConstants.compressionCodec, orcSettings.compressionType);
        }
    }
}
